import { Footer, Header, Preloader, Skynity } from 'components';
import { NetworksParams } from 'config';
import {
  createWeb3ModalOptionsEIP6963,
  isPermittedNetwork,
  web3ModalAutoWalletConnect,
} from 'helpers';
import React, { Fragment, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllocationPools,
  getGameBridgeContract,
  getLPPairsContracts,
  getNftTokenContract,
  getStablecoinPrices,
  getStakingPools,
  getTokensData,
  getVestingPools,
  logout,
  selectInitializingFinished,
  selectShowLoader,
  selectWeb3,
  selectWeb3Modal,
  selectWeb3Provider,
  setConnectedAddress,
  setInitialData,
  setInitializingFinished,
  setShowLoader,
  setWeb3Modal,
  setWeb3Provider,
} from 'store';
import Web3 from 'web3';
import Web3Modal from 'web3modal';

import {
  EIP6963ProviderDetail,
  useEip6963Providers,
} from './helpers/eip6963.helper';

const App: React.FC = () => {
  const dispatch = useDispatch();
  const showLoader = useSelector(selectShowLoader);
  const web3: Web3 = useSelector(selectWeb3);
  const web3Modal: Web3Modal = useSelector(selectWeb3Modal);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const web3Provider: any = useSelector(selectWeb3Provider);
  const initializingFinished = useSelector(selectInitializingFinished);

  const eip6963Providers = useEip6963Providers();
  const eip6963ProvidersRef = useRef<EIP6963ProviderDetail[]>([]);

  useEffect(() => {
    eip6963ProvidersRef.current = eip6963Providers;
  });

  useEffect(() => {
    if (web3Modal) {
      // Show web3modal on initial page load (or auto reconnect if cached provider)
      if (web3ModalAutoWalletConnect && web3Modal.cachedProvider) {
        web3Modal.connect();
      } else if (!web3Modal.cachedProvider) {
        dispatch(setShowLoader(false));
      }
    } else {
      setTimeout(() => {
        // Initialize web3modal
        const web3Modal: Web3Modal = new Web3Modal(
          createWeb3ModalOptionsEIP6963(eip6963ProvidersRef.current)
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        web3Modal.on('connect', (provider: any) => {
          dispatch(setWeb3Provider(provider));
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        web3Modal.on('error', (error: any) => {
          // eslint-disable-next-line no-console
          console.error('ERROR@connectWallet:', error);
          dispatch(setShowLoader(false));
        });
        dispatch(setWeb3Modal(web3Modal));
      }, 500);
    }
  }, [web3Modal]);

  useEffect(() => {
    if (web3Provider) {
      initEvmChainData();
    }
  }, [web3Provider]);

  const initEvmChainData = async (): Promise<void> => {
    try {
      const web3 = new Web3(web3Provider);
      web3.eth.handleRevert = true;
      const gasPrice = await web3.eth.getGasPrice();
      const networkId: number = await web3.eth.net.getId();
      const networkIdString: string = networkId.toString();
      const accounts = await web3.eth.getAccounts();

      web3Provider.on('accountsChanged', () => {
        dispatch(logout());
      });
      web3Provider.on('chainChanged', () => {
        dispatch(logout());
      });
      web3Provider.on(
        'disconnect',
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {
          web3Modal.clearCachedProvider();
          dispatch(logout());
        }
      );

      dispatch(
        setInitialData({
          web3,
          gasPrice,
          connectedNetwork: Object.values(NetworksParams).find(
            (network) => network.chainId == networkId
          ),
          connectedAddress: accounts[0],
        })
      );

      if (isPermittedNetwork(networkId)) {
        dispatch(getStablecoinPrices());
        dispatch(getTokensData({ networkId: networkIdString, web3 }));
        dispatch(getLPPairsContracts({ networkId: networkIdString, web3 }));
        dispatch(getNftTokenContract({ networkId: networkIdString, web3 }));
        dispatch(getGameBridgeContract({ networkId: networkIdString, web3 }));
        dispatch(
          getStakingPools({
            networkId: networkIdString,
            connectedAddress: accounts[0],
            web3,
          })
        );
        dispatch(
          getVestingPools({
            networkId: networkIdString,
            connectedAddress: accounts[0],
            web3,
          })
        );
        dispatch(
          getAllocationPools({
            networkId: networkIdString,
            web3,
            connectedAddress: accounts[0],
          })
        );
      }

      dispatch(setInitializingFinished(true));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    dispatch(setShowLoader(false));
  };

  const connectWallet = async (): Promise<void> => {
    web3Modal.clearCachedProvider();
    web3Modal.toggleModal();
  };

  const disconnectWallet = async (): Promise<void> => {
    dispatch(setWeb3Provider(null));
    dispatch(setConnectedAddress(null));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((web3.currentProvider as any).disconnect) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (web3.currentProvider as any).disconnect();
    }

    web3Modal.clearCachedProvider();
    window.location.reload();
  };

  return (
    <Fragment>
      {showLoader && <Preloader transparent={initializingFinished} />}
      <div className="app-container">
        <Header
          connectWallet={connectWallet}
          disconnectWallet={disconnectWallet}
        />
        <Skynity />
      </div>
      <Footer />
    </Fragment>
  );
};

export default App;
